/**
 * Chatbot Domain Schemas
 *
 * Zod schemas for Message entity validation and type inference.
 * Single source of truth for types AND validation rules.
 */
import { z } from "zod";
import { translateApp } from "@/i18n";
import { ItineraryDraftSchema } from "./chat-api.schema";
// =============================================================================
// ENUMS
// =============================================================================
export const MessageRoleSchema = z.enum(["user", "assistant"]);
export const MessageStatusSchema = z.enum(["sending", "sent", "failed"]);
// =============================================================================
// MESSAGE SCHEMA
// =============================================================================
export const messageSchema = z.object({
    id: z.string().min(1),
    role: MessageRoleSchema,
    content: z.string(),
    timestamp: z.date(),
    status: MessageStatusSchema.optional(),
    draft: ItineraryDraftSchema.optional(),
});
// =============================================================================
// MESSAGE INPUT SCHEMA
// =============================================================================
export const messageInputSchema = z.object({
    content: z
        .string()
        .min(1, translateApp("chatbot.validation.empty_message"))
        .max(2000, translateApp("chatbot.validation.max_message")),
});
/**
 * Quick action that starts itinerary creation. Hidden for roles that cannot
 * create itineraries (e.g. provider organizations).
 */
export const ITINERARY_QUICK_ACTION_ID = "create-itinerary";
const defaultQuickActionKeys = [
    {
        id: ITINERARY_QUICK_ACTION_ID,
        key: "chatbot.quick_actions.create_itinerary",
    },
    {
        id: "manage-providers",
        key: "chatbot.quick_actions.manage_providers",
    },
    {
        id: "configure-commissions",
        key: "chatbot.quick_actions.configure_commissions",
    },
];
export function getDefaultQuickActions() {
    return defaultQuickActionKeys.map((action) => ({
        id: action.id,
        label: translateApp(action.key),
        prompt: translateApp(action.key),
    }));
}
export const defaultQuickActions = getDefaultQuickActions();
